import { useState, useEffect } from 'react'
import { User } from '../../types'
import { Role } from '@/features/role/types'
import { useRoles } from '@/features/role'
import { Separator } from '@/ui/separator'
import { Input } from '@/ui/input'
import { Label } from '@/ui/label'
import { Button } from '@/ui/button'
import { MaterialIcon } from '@/ui/material-icon'
import styles from './MapUserRole.module.scss'

interface MapUserRoleFormProps {
    user: User
    onCancel: () => void
    onSubmit: (roleIds: number[]) => void
    isLoading?: boolean
}

export default function MapUserRoleForm({
    user,
    onCancel,
    onSubmit,
    isLoading = false,
}: MapUserRoleFormProps) {
    const { data: roles = [], isLoading: rolesLoading } = useRoles({ is_active: true })
    const availableRoles: Role[] = roles

    const [selectedRoles, setSelectedRoles] = useState<Role[]>([])
    const [selectValue, setSelectValue] = useState('')

    useEffect(() => {
        if (availableRoles.length && user?.role) {
            const matched = availableRoles.filter(
                r => r.label === user.role || r.name === user.role
            )
            if (matched.length) setSelectedRoles(matched)
        }
    }, [availableRoles, user?.role])

    const handleSelectRole = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value
        const role = availableRoles.find(r => r.id === id)
        if (role && !selectedRoles.find(r => r.id === id)) {
            setSelectedRoles(prev => [...prev, role])
        }
        setSelectValue('')
    }

    const handleRemoveRole = (id: string) => {
        setSelectedRoles(prev => prev.filter(r => r.id !== id))
    }

    const handleSubmit = () => {
        const roleIds = selectedRoles
            .map(r => Number(r.id))
            .filter(id => !Number.isNaN(id))

        if (roleIds.length === 0) {
            return
        }

        onSubmit(roleIds)
    }

    return (
        <>
            <Separator />
            <h1 className={styles.title}>Map Role to User</h1>
            <Separator />

            <div className={styles.cardBody}>
                <div className={styles.userInfo}>

                    <div className={styles.formField}>
                        <Label className={styles.fieldLabel}>First Name</Label>
                        <Input
                            value={user?.name?.split(' ')[0] ?? ''}
                            readOnly
                            className={styles.fieldInput}
                        />
                    </div>

                    <div className={styles.formField}>
                        <Label className={styles.fieldLabel}>Last Name</Label>
                        <Input
                            value={user?.name?.split(' ').slice(1).join(' ') ?? ''}
                            readOnly
                            className={styles.fieldInput}
                        />
                    </div>

                    <div className={styles.formField}>
                        <Label className={styles.fieldLabel}>Email/Mobile</Label>
                        <Input
                            value={user?.email ?? user?.mobile ?? ''}
                            readOnly
                            className={styles.fieldInput}
                        />
                    </div>

                    <div className={styles.formField}>
                        <Label className={styles.fieldLabel}>
                            Roles <span className={styles.required}>*</span>
                        </Label>
                        <select
                            className={styles.select}
                            value={selectValue}
                            onChange={handleSelectRole}
                            disabled={rolesLoading || isLoading}
                        >
                            <option value="" disabled>
                                {rolesLoading ? 'Loading roles...' : 'Select Roles'}
                            </option>
                            {availableRoles
                                .filter(r => !selectedRoles.find(s => s.id === r.id))
                                .map(r => (
                                    <option key={r.id} value={r.id}>
                                        {r.label || r.name}
                                    </option>
                                ))}
                        </select>

                        {selectedRoles.length > 0 && (
                            <div className={styles.selectedRoles}>
                                {selectedRoles.map(role => (
                                    <span key={role.id} className={styles.roleTag}>
                                        {role.label || role.name}
                                        <button
                                            className={styles.removeRole}
                                            onClick={() => handleRemoveRole(role.id)}
                                            disabled={isLoading}
                                            aria-label={`Remove ${role.label || role.name}`}
                                        >
                                            <MaterialIcon name="close" size={14} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className={styles.formActions}>
                    <Button variant="outline" className={styles. cnloutlineCustom} onClick={onCancel} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={selectedRoles.length === 0 || isLoading}
                    >
                        {isLoading ? 'Mapping...' : 'Map Roles'}
                    </Button>
                </div>
            </div>
        </>
    )
}